import {
  formatSig,
  formatSigned,
  parseNumericAnswer,
  type AnswerCheck,
  type DataSource,
  type ReviewStatus,
} from './energyCalorimetry';

// ---------------------------------------------------------------------------
// Senior extension: entropy, Gibbs free energy and spontaneity
// (study-chemistry-tools-prd.md §8.2 `energy-calorimetry` → `spontaneity`)
// ---------------------------------------------------------------------------

export const SPONTANEITY_SOURCES: Record<'nist' | 'crc', DataSource> = {
  nist: {
    label: 'NIST Chemistry WebBook (SRD 69) and CODATA Key Values',
    detail:
      'Standard enthalpies of formation and standard molar entropies at 298.15 K and 1 bar, combined by Hess’s law.',
  },
  crc: {
    label: 'CRC Handbook of Chemistry and Physics',
    detail:
      'Enthalpy of fusion of water at 0 °C and enthalpy of solution of ammonium nitrate at 25 °C.',
  },
};

export const SPONTANEITY_CONDITIONS =
  'ΔH° and ΔS° are standard values at 298 K and 100 kPa unless a reaction states otherwise. The model assumes both stay constant as the temperature changes.';

/** Short label for the extension topic. Other jurisdictions may not assess Gibbs free energy. */
export const SPONTANEITY_EXTENSION_NOTE =
  'Extension topic: entropy and Gibbs free energy are part of NSW Year 11 Module 4 (Drivers of reactions). Other states may not assess this.';

/** Unit conversion: ΔS is tabulated in J K⁻¹ mol⁻¹; ΔG and ΔH use kJ mol⁻¹. */
export const JOULES_PER_KILOJOULE = 1000;
export const KELVIN_OFFSET = 273.15;
/** Temperatures the model accepts, K. Outside this range the 298 K data are not trusted. */
export const MIN_TEMPERATURE_K = 200;
export const MAX_TEMPERATURE_K = 2000;
export const DEFAULT_TEMPERATURE_K = 298;
/** |ΔG| below this (kJ mol⁻¹) is treated as "close to equilibrium" at that temperature. */
export const NEAR_EQUILIBRIUM_KJ = 0.5;

const RELATIVE_TOLERANCE = 0.02;
const ABSOLUTE_TOLERANCE_KJ = 0.5;
const CROSSOVER_RELATIVE_TOLERANCE = 0.02;
const CROSSOVER_ABSOLUTE_TOLERANCE_K = 2;

const REVIEW_STATUS: ReviewStatus = 'pending-teaching-review';

export type SignCase = 'always' | 'low-temperature' | 'high-temperature' | 'never';
export type SpontaneityVerdict = 'spontaneous' | 'non-spontaneous' | 'near-equilibrium';

export interface SpontaneityReaction {
  id: string;
  name: string;
  equation: string;
  /** Standard enthalpy change, kJ mol⁻¹ (per mole of reaction as written). */
  deltaHkJ: number;
  /** Standard entropy change, J K⁻¹ mol⁻¹ (per mole of reaction as written). */
  deltaSJPerK: number;
  /** Temperature and pressure the data refer to. */
  conditions: string;
  /** What the entropy sign means in particle terms. */
  entropyReason: string;
  /** Where the constant-ΔH/ΔS model differs from the real system. */
  boundary: string;
  sources: DataSource[];
  reviewStatus: ReviewStatus;
}

const STANDARD_298 = '298 K, 100 kPa (standard state)';

export const SPONTANEITY_REACTIONS: SpontaneityReaction[] = [
  {
    id: 'calcium-carbonate-decomposition',
    name: 'Decomposing limestone',
    equation: 'CaCO₃(s) → CaO(s) + CO₂(g)',
    deltaHkJ: 178,
    deltaSJPerK: 161,
    conditions: STANDARD_298,
    entropyReason:
      'A solid forms a gas, so the particles have many more ways to spread out: ΔS > 0.',
    boundary:
      'Lime kilns run near 900 °C. The predicted crossover (about 830 °C) is close because ΔH and ΔS change only slowly with temperature.',
    sources: [SPONTANEITY_SOURCES.nist],
    reviewStatus: REVIEW_STATUS,
  },
  {
    id: 'ammonium-nitrate-dissolving',
    name: 'Ammonium nitrate dissolving',
    equation: 'NH₄NO₃(s) → NH₄⁺(aq) + NO₃⁻(aq)',
    deltaHkJ: 25.7,
    deltaSJPerK: 108.7,
    conditions: '298 K, 100 kPa, ions at standard concentration',
    entropyReason: 'An ordered crystal breaks into ions that move through the water: ΔS > 0.',
    boundary:
      'This is why a cold pack works: the entropy gain outweighs the energy taken in at room temperature.',
    sources: [SPONTANEITY_SOURCES.crc, SPONTANEITY_SOURCES.nist],
    reviewStatus: REVIEW_STATUS,
  },
  {
    id: 'water-vaporisation',
    name: 'Water boiling',
    equation: 'H₂O(l) → H₂O(g)',
    deltaHkJ: 44.0,
    deltaSJPerK: 118.9,
    conditions: STANDARD_298,
    entropyReason:
      'A liquid becomes a gas, so the molecules spread through a much larger volume: ΔS > 0.',
    boundary:
      'The model predicts about 370 K. Water really boils at 373 K at 101.3 kPa: ΔH and ΔS measured at 298 K drift a little by 373 K.',
    sources: [SPONTANEITY_SOURCES.nist],
    reviewStatus: REVIEW_STATUS,
  },
  {
    id: 'ice-melting',
    name: 'Ice melting',
    equation: 'H₂O(s) → H₂O(l)',
    deltaHkJ: 6.01,
    deltaSJPerK: 22.0,
    conditions: 'Data at 273.15 K and 101.3 kPa (the normal melting point), not 298 K',
    entropyReason:
      'Molecules leave fixed positions in the lattice and move past each other: ΔS > 0.',
    boundary:
      'At exactly 273 K, ΔG ≈ 0 and ice and water coexist. Above it ice melts; below it water freezes.',
    sources: [SPONTANEITY_SOURCES.crc],
    reviewStatus: REVIEW_STATUS,
  },
  {
    id: 'dinitrogen-tetroxide',
    name: 'Dinitrogen tetroxide dissociating',
    equation: 'N₂O₄(g) ⇌ 2NO₂(g)',
    deltaHkJ: 57.2,
    deltaSJPerK: 175.8,
    conditions: STANDARD_298,
    entropyReason: 'One mole of gas becomes two moles of gas: ΔS > 0.',
    boundary:
      'ΔG° near zero means a real equilibrium mixture, not “all or nothing”. Warming the brown gas makes it darker.',
    sources: [SPONTANEITY_SOURCES.nist],
    reviewStatus: REVIEW_STATUS,
  },
  {
    id: 'ammonia-synthesis',
    name: 'Making ammonia (Haber process)',
    equation: 'N₂(g) + 3H₂(g) → 2NH₃(g)',
    deltaHkJ: -92.2,
    deltaSJPerK: -198.7,
    conditions: STANDARD_298,
    entropyReason: 'Four moles of gas become two moles of gas: ΔS < 0.',
    boundary:
      'Industry runs near 400–450 °C, above the crossover, and accepts a smaller yield in exchange for a faster rate and recycling of unreacted gas.',
    sources: [SPONTANEITY_SOURCES.nist],
    reviewStatus: REVIEW_STATUS,
  },
  {
    id: 'hydrogen-combustion',
    name: 'Hydrogen burning in oxygen',
    equation: '2H₂(g) + O₂(g) → 2H₂O(l)',
    deltaHkJ: -571.6,
    deltaSJPerK: -326.6,
    conditions: STANDARD_298,
    entropyReason: 'Three moles of gas become a liquid: ΔS < 0.',
    boundary:
      'The predicted crossover (about 1750 K) is not physically meaningful: long before then the water is a gas, so these 298 K liquid-water data no longer apply.',
    sources: [SPONTANEITY_SOURCES.nist],
    reviewStatus: REVIEW_STATUS,
  },
  {
    id: 'methane-combustion',
    name: 'Methane burning',
    equation: 'CH₄(g) + 2O₂(g) → CO₂(g) + 2H₂O(l)',
    deltaHkJ: -890.3,
    deltaSJPerK: -242.9,
    conditions: STANDARD_298,
    entropyReason: 'Three moles of gas become one mole of gas and a liquid: ΔS < 0.',
    boundary:
      'Spontaneous does not mean fast: natural gas needs a spark to overcome its activation energy. The crossover (about 3700 K) is far outside the model.',
    sources: [SPONTANEITY_SOURCES.nist],
    reviewStatus: REVIEW_STATUS,
  },
  {
    id: 'hydrogen-peroxide-decomposition',
    name: 'Hydrogen peroxide decomposing',
    equation: '2H₂O₂(l) → 2H₂O(l) + O₂(g)',
    deltaHkJ: -196.0,
    deltaSJPerK: 125.8,
    conditions: STANDARD_298,
    entropyReason: 'A gas forms from liquids: ΔS > 0.',
    boundary:
      'Spontaneous at every temperature, yet a bottle lasts for months because the uncatalysed reaction is slow.',
    sources: [SPONTANEITY_SOURCES.nist],
    reviewStatus: REVIEW_STATUS,
  },
  {
    id: 'ozone-formation',
    name: 'Making ozone from oxygen',
    equation: '3O₂(g) → 2O₃(g)',
    deltaHkJ: 285.4,
    deltaSJPerK: -137.6,
    conditions: STANDARD_298,
    entropyReason: 'Three moles of gas become two moles of gas: ΔS < 0.',
    boundary:
      'Ozone still forms in the stratosphere and in electric discharges because UV light or electricity supplies the energy. ΔG > 0 only says it will not happen on its own.',
    sources: [SPONTANEITY_SOURCES.nist],
    reviewStatus: REVIEW_STATUS,
  },
];

export const SIGN_CASE_LABELS: Record<SignCase, string> = {
  always: 'Spontaneous at every temperature',
  'low-temperature': 'Spontaneous only at low temperature',
  'high-temperature': 'Spontaneous only at high temperature',
  never: 'Never spontaneous on its own',
};

export const SIGN_CASE_SIGNS: Record<SignCase, string> = {
  always: 'ΔH < 0 and ΔS > 0',
  'low-temperature': 'ΔH < 0 and ΔS < 0',
  'high-temperature': 'ΔH > 0 and ΔS > 0',
  never: 'ΔH > 0 and ΔS < 0',
};

export const VERDICT_LABELS: Record<SpontaneityVerdict, string> = {
  spontaneous: 'Spontaneous (ΔG < 0)',
  'non-spontaneous': 'Not spontaneous (ΔG > 0)',
  'near-equilibrium': 'Close to equilibrium (ΔG ≈ 0)',
};

export function signCase(reaction: SpontaneityReaction): SignCase | null {
  const { deltaHkJ: h, deltaSJPerK: s } = reaction;
  if (!Number.isFinite(h) || !Number.isFinite(s) || h === 0 || s === 0) return null;
  if (h < 0 && s > 0) return 'always';
  if (h < 0 && s < 0) return 'low-temperature';
  if (h > 0 && s > 0) return 'high-temperature';
  return 'never';
}

export function isSupportedTemperature(temperatureK: number): boolean {
  return (
    Number.isFinite(temperatureK) &&
    temperatureK >= MIN_TEMPERATURE_K &&
    temperatureK <= MAX_TEMPERATURE_K
  );
}

/** −TΔS in kJ mol⁻¹, after converting ΔS from J to kJ. */
export function entropyTermKJ(reaction: SpontaneityReaction, temperatureK: number): number {
  return (-temperatureK * reaction.deltaSJPerK) / JOULES_PER_KILOJOULE;
}

/** ΔG = ΔH − TΔS in kJ mol⁻¹. Fails closed (null) outside the supported temperature range. */
export function gibbsFreeEnergy(
  reaction: SpontaneityReaction,
  temperatureK: number,
): number | null {
  if (!isSupportedTemperature(temperatureK)) return null;
  return reaction.deltaHkJ + entropyTermKJ(reaction, temperatureK);
}

export function spontaneityVerdict(deltaGkJ: number): SpontaneityVerdict {
  if (Math.abs(deltaGkJ) < NEAR_EQUILIBRIUM_KJ) return 'near-equilibrium';
  return deltaGkJ < 0 ? 'spontaneous' : 'non-spontaneous';
}

/** T = ΔH / ΔS in K (ΔS converted to kJ). Null when the signs differ: no crossover exists. */
export function crossoverTemperatureK(reaction: SpontaneityReaction): number | null {
  const kind = signCase(reaction);
  if (kind !== 'low-temperature' && kind !== 'high-temperature') return null;
  return (reaction.deltaHkJ * JOULES_PER_KILOJOULE) / reaction.deltaSJPerK;
}

function near(value: number, target: number, relative: number, absolute: number): boolean {
  return Math.abs(value - target) <= Math.max(Math.abs(target) * relative, absolute);
}

function nearKJ(value: number, target: number): boolean {
  return near(value, target, RELATIVE_TOLERANCE, ABSOLUTE_TOLERANCE_KJ);
}

export function gibbsWorking(reaction: SpontaneityReaction, temperatureK: number): string | null {
  const deltaG = gibbsFreeEnergy(reaction, temperatureK);
  if (deltaG === null) return null;
  const deltaSkJ = reaction.deltaSJPerK / JOULES_PER_KILOJOULE;
  return `ΔG = ΔH − TΔS = ${formatSigned(reaction.deltaHkJ, 3)} kJ mol⁻¹ − (${temperatureK} K × ${formatSigned(deltaSkJ, 4)} kJ K⁻¹ mol⁻¹) = ${formatSigned(deltaG, 3)} kJ mol⁻¹`;
}

export function checkSignCasePrediction(
  reaction: SpontaneityReaction,
  prediction: SignCase,
): AnswerCheck {
  const actual = signCase(reaction);
  if (actual === null) {
    return { verdict: 'invalid', feedback: 'This reaction has no reviewed sign case.' };
  }
  const signs = `Here ${SIGN_CASE_SIGNS[actual]}.`;
  if (prediction === actual) {
    const why =
      actual === 'always'
        ? 'Both terms make ΔG negative, so no temperature can make it positive.'
        : actual === 'never'
          ? 'Both terms make ΔG positive, so no temperature can make it negative.'
          : actual === 'low-temperature'
            ? 'ΔH favours the reaction, but −TΔS grows more positive as T rises, so it only wins at low temperature.'
            : 'ΔH works against the reaction, but −TΔS grows more negative as T rises, so it only wins at high temperature.';
    return { verdict: 'correct', feedback: `Correct. ${signs} ${why}` };
  }
  return {
    verdict: 'sign',
    feedback: `${signs} ${reaction.entropyReason} That makes it “${SIGN_CASE_LABELS[actual].toLowerCase()}”, not “${SIGN_CASE_LABELS[prediction].toLowerCase()}”.`,
  };
}

export function checkVerdictPrediction(
  reaction: SpontaneityReaction,
  temperatureK: number,
  prediction: SpontaneityVerdict,
): AnswerCheck {
  const deltaG = gibbsFreeEnergy(reaction, temperatureK);
  if (deltaG === null) {
    return {
      verdict: 'invalid',
      feedback: `Choose a temperature from ${MIN_TEMPERATURE_K} K to ${MAX_TEMPERATURE_K} K. Outside that range the 298 K data are not reliable.`,
    };
  }
  const actual = spontaneityVerdict(deltaG);
  const value = `At ${temperatureK} K, ΔG = ${formatSigned(deltaG, 3)} kJ mol⁻¹.`;
  if (prediction === actual) {
    return {
      verdict: 'correct',
      feedback:
        actual === 'near-equilibrium'
          ? `Correct. ${value} This is about the crossover temperature, so forward and reverse processes are balanced.`
          : `Correct. ${value} ${actual === 'spontaneous' ? 'A negative ΔG means the forward reaction can proceed on its own.' : 'A positive ΔG means the reverse reaction is favoured; the forward reaction needs a continuous energy input.'}`,
    };
  }
  const crossover = crossoverTemperatureK(reaction);
  const hint =
    crossover !== null
      ? ` The crossover is about ${formatSig(crossover, 3)} K; compare it with ${temperatureK} K.`
      : ` ${SIGN_CASE_SIGNS[signCase(reaction) ?? 'always']}, so the temperature cannot change the sign of ΔG.`;
  return {
    verdict: 'other',
    feedback: `${value} That is ${VERDICT_LABELS[actual].toLowerCase()}, not ${VERDICT_LABELS[prediction].toLowerCase()}.${hint}`,
  };
}

/** Checks a learner ΔG (kJ mol⁻¹), diagnosing the J/kJ mismatch, °C and sign errors. */
export function checkGibbsAnswer(
  reaction: SpontaneityReaction,
  temperatureK: number,
  raw: string,
): AnswerCheck {
  const deltaG = gibbsFreeEnergy(reaction, temperatureK);
  const working = gibbsWorking(reaction, temperatureK);
  if (deltaG === null || working === null) {
    return {
      verdict: 'invalid',
      feedback: `The model only supports ${MIN_TEMPERATURE_K}–${MAX_TEMPERATURE_K} K.`,
    };
  }
  const value = parseNumericAnswer(raw);
  if (value === null) {
    return {
      verdict: 'invalid',
      feedback: 'Enter ΔG as a number in kJ mol⁻¹ with its sign, for example −32.9.',
    };
  }
  if (nearKJ(value, deltaG)) return { verdict: 'correct', feedback: `Correct. ${working}.` };

  const mixedUnits = reaction.deltaHkJ - temperatureK * reaction.deltaSJPerK;
  if (nearKJ(value, mixedUnits)) {
    return {
      verdict: 'units',
      feedback: `Unit mismatch: ΔH is in kJ but ΔS is in J K⁻¹. Divide ΔS by 1000 first (${reaction.deltaSJPerK} J K⁻¹ mol⁻¹ = ${formatSig(reaction.deltaSJPerK / JOULES_PER_KILOJOULE, 4)} kJ K⁻¹ mol⁻¹). ${working}.`,
    };
  }
  if (nearKJ(value, deltaG * JOULES_PER_KILOJOULE)) {
    return {
      verdict: 'units',
      feedback: `That value is in J mol⁻¹. Give ΔG in kJ mol⁻¹. ${working}.`,
    };
  }
  const celsius = temperatureK - KELVIN_OFFSET;
  const celsiusValue = reaction.deltaHkJ - (celsius * reaction.deltaSJPerK) / JOULES_PER_KILOJOULE;
  if (Math.abs(celsius) > 1 && nearKJ(value, celsiusValue) && !nearKJ(celsiusValue, deltaG)) {
    return {
      verdict: 'celsius',
      feedback: `It looks like you used ${formatSig(celsius, 3)} °C. Entropy calculations need the absolute temperature in kelvin: T = ${temperatureK} K. ${working}.`,
    };
  }
  const added = reaction.deltaHkJ - entropyTermKJ(reaction, temperatureK);
  if (nearKJ(value, added) && !nearKJ(added, deltaG)) {
    return {
      verdict: 'sign',
      feedback: `Check the sign of the TΔS term: ΔG = ΔH − TΔS, so subtract T × ΔS (with ΔS’s own sign). ${working}.`,
    };
  }
  return { verdict: 'other', feedback: `Not yet. ${working}.` };
}

/** Checks a learner crossover temperature in K. */
export function checkCrossoverAnswer(reaction: SpontaneityReaction, raw: string): AnswerCheck {
  const crossover = crossoverTemperatureK(reaction);
  if (crossover === null) {
    return {
      verdict: 'invalid',
      feedback: `No crossover exists: ${SIGN_CASE_SIGNS[signCase(reaction) ?? 'always']}, so ΔG keeps the same sign at every temperature.`,
    };
  }
  const value = parseNumericAnswer(raw);
  if (value === null) {
    return {
      verdict: 'invalid',
      feedback: 'Enter the crossover temperature as a number in kelvin.',
    };
  }
  const working = `T = ΔH ÷ ΔS = (${formatSigned(reaction.deltaHkJ, 3)} × 1000 J mol⁻¹) ÷ (${formatSigned(reaction.deltaSJPerK, 4)} J K⁻¹ mol⁻¹) = ${formatSig(crossover, 3)} K`;
  if (near(value, crossover, CROSSOVER_RELATIVE_TOLERANCE, CROSSOVER_ABSOLUTE_TOLERANCE_K)) {
    return { verdict: 'correct', feedback: `Correct. ${working}. At this temperature ΔG = 0.` };
  }
  const unconverted = crossover / JOULES_PER_KILOJOULE;
  if (
    near(
      value,
      unconverted,
      CROSSOVER_RELATIVE_TOLERANCE,
      unconverted * CROSSOVER_RELATIVE_TOLERANCE,
    )
  ) {
    return {
      verdict: 'units',
      feedback: `Unit mismatch: you divided kJ by J K⁻¹. Convert ΔH to J (× 1000) or ΔS to kJ (÷ 1000) first. ${working}.`,
    };
  }
  if (
    near(
      value,
      crossover - KELVIN_OFFSET,
      CROSSOVER_RELATIVE_TOLERANCE,
      CROSSOVER_ABSOLUTE_TOLERANCE_K,
    )
  ) {
    return {
      verdict: 'celsius',
      feedback: `That is the crossover in °C. The question asks for kelvin: add 273. ${working}.`,
    };
  }
  if (near(value, -crossover, CROSSOVER_RELATIVE_TOLERANCE, CROSSOVER_ABSOLUTE_TOLERANCE_K)) {
    return { verdict: 'sign', feedback: `A temperature in kelvin cannot be negative. ${working}.` };
  }
  return { verdict: 'other', feedback: `Not yet. ${working}.` };
}

/** Points for a ΔG–T line across the supported range (straight line under the model). */
export function gibbsLine(
  reaction: SpontaneityReaction,
): { temperatureK: number; deltaGkJ: number }[] {
  return [MIN_TEMPERATURE_K, MAX_TEMPERATURE_K].map((temperatureK) => ({
    temperatureK,
    deltaGkJ: reaction.deltaHkJ + entropyTermKJ(reaction, temperatureK),
  }));
}

export const SPONTANEITY_BOUNDARIES: string[] = [
  'The model treats ΔH and ΔS as constant, so ΔG changes in a straight line with T. Real values drift with temperature, so crossover temperatures are estimates.',
  'ΔG° describes standard conditions (100 kPa, 1 mol L⁻¹). Changing pressure or concentration changes ΔG.',
  'Spontaneous means thermodynamically favoured, not fast. Rate depends on activation energy.',
];
